import React, { createContext, useContext, useState, useCallback } from "react";

/**
 * LanguageContext - FR / EN translations for the MAG Protection website.
 * Default language: FR.
 */

export const translations = {
  FR: {
    nav: {
      about: "À propos",
      services: "Nos Services",
      partners: "Nos Partenaires",
      contact: "Contact"
    },
    hero: {
      title: "Bienvenue chez MAG Protection",
      subtitle: "Votre sécurité, notre priorité absolue",
      ctaContact: "Nous Contacter",
      ctaServices: "Nos Services"
    },
    servicesBanner: {
      title: "Explore Nos Services"
    },
    services: {
      eventSecurity: {
        name: "Évènementielle",
        description: "Protection et gestion de la sécurité pour tous types d'événements."
      },
      guarding: {
        name: "Gardiennage",
        description: "Services de gardiennage professionnel 24h/24."
      },
      closeProtection: {
        name: "Protection Rapprochée",
        description: "Protection rapprochée pour VIP et personnalités."
      },
      patrols: {
        name: "Rondes & Interventions",
        description: "Rondes de sécurité et interventions rapides."
      },
      nightSurveillance: {
        name: "Surveillance Nocturne",
        description: "Surveillance nocturne pour particuliers et entreprises."
      },
      constructionSite: {
        name: "Chantiers",
        description: "Sécurisation de chantiers et sites industriels."
      },
      steward: {
        name: "Steward / Stadier",
        description: "Stewards et sécurité pour stades et grands événements sportifs."
      },
      secureTransport: {
        name: "Transport Sécurisé",
        description: "Transport sécurisé de biens et de valeurs."
      },
      cameraInstall: {
        name: "Installation Caméras",
        description: "Installation de systèmes de vidéosurveillance."
      },
      accessControl: {
        name: "Contrôle d'Accès",
        description: "Systèmes de contrôle d'accès modernes."
      },
      k9: {
        name: "Maître Chien",
        description: "Équipes cynophiles avec chiens de sécurité."
      },
      remoteMonitoring: {
        name: "Télésurveillance",
        description: "Télésurveillance 24/7 depuis notre centre de contrôle."
      }
    },
    about: {
      titleParts: { prefix: "Qui ", highlight: "sommes", suffix: " nous?" },
      p1: "MAG Protection (Multy Assistance Group) se définit comme le groupe de référence en matière de sécurité et d'assistance au Luxembourg. Spécialisée dans la protection des personnes et des biens, notre société intervient dans tous les secteurs où la sécurité est une priorité stratégique.",
      p2: "Fondée au Luxembourg, MAG Protection opère sous licence de gardiennage délivrée par l'État luxembourgeois. Notre équipe d'agents qualifiés assure une présence 24h/24 et 7j/7 pour garantir la sécurité de nos clients particuliers, entreprises et institutions."
    },
    stats: {
      missions: "Missions Sécurisées",
      experience: "Ans d'Expérience",
      availability: "Disponibilité",
      clients: "Clients Satisfaits"
    },
    testimonials: {
      titleParts: { prefix: "Ils nous font ", highlight: "confiance", suffix: "" },
      thomas: {
        role: "Directeur Commercial",
        quote: "MAG Protection assure la sécurité de nos événements depuis 3 ans. Professionnalisme exemplaire et réactivité sans faille. Je recommande vivement."
      },
      sophie: {
        role: "Gestionnaire de Résidence",
        quote: "Grâce aux agents de MAG Protection, nos résidents se sentent en sécurité. Service impeccable, personnel courtois et efficace."
      },
      jean: {
        role: "Organisateur d'Événements",
        quote: "Pour nos grands événements au Luxembourg, MAG Protection est notre partenaire incontournable. Toujours présents, toujours professionnels."
      }
    },
    emergency: {
      title: "Besoin d'une Intervention Rapide?",
      subtitle: "Notre équipe d'intervention est disponible 24h/24 et 7j/7",
      ctaEmergency: "Intervention d'urgence:"
    },
    partners: {
      titleParts: { prefix: "Nos ", highlight: "Partenaires", suffix: "" }
    },
    contact: {
      titleParts: { prefix: "Nous ", highlight: "Contacter", suffix: "" },
      phoneLabel: "Téléphone",
      emailLabel: "Email",
      form: {
        heading: "Envoyez-nous un message",
        subheading: "Remplissez le formulaire ci-dessous, nous vous répondrons rapidement.",
        firstname: "Prénom",
        lastname: "Nom",
        company: "Nom de société",
        companyOptional: "(facultatif)",
        phone: "Téléphone",
        phonePlaceholder: "+352 661 000 000",
        email: "Email",
        message: "Message",
        messagePlaceholder: "Décrivez votre besoin...",
        consent: "J'accepte que mes données soient utilisées pour traiter ma demande, conformément à la politique de confidentialité.",
        submit: "Envoyer le message",
        submitting: "Envoi en cours...",
        successTitle: "Message envoyé !",
        successBody: "Merci de nous avoir contactés. Nous reviendrons vers vous dans les plus brefs délais.",
        newMessage: "Envoyer un autre message",
        errors: {
          firstname: "Le prénom est obligatoire.",
          lastname: "Le nom est obligatoire.",
          phone: "Numéro de téléphone invalide.",
          email: "Adresse email invalide.",
          message: "Le message doit contenir au moins 5 caractères.",
          consent: "Vous devez accepter le traitement de vos données.",
          rateLimit: "Trop de messages envoyés. Veuillez réessayer dans quelques minutes.",
          generic: "Une erreur est survenue. Veuillez réessayer."
        }
      }
    },
    footer: {
      text: "Powered by MAG Protection © 2024 | magprotection.lu"
    }
  },

  EN: {
    nav: {
      about: "About",
      services: "Our Services",
      partners: "Our Partners",
      contact: "Contact"
    },
    hero: {
      title: "Welcome to MAG Protection",
      subtitle: "Your security, our absolute priority",
      ctaContact: "Contact Us",
      ctaServices: "Our Services"
    },
    servicesBanner: {
      title: "Explore Our Services"
    },
    services: {
      eventSecurity: {
        name: "Event Security",
        description: "Comprehensive security and safety management for all types of events."
      },
      guarding: {
        name: "Guarding",
        description: "Professional 24/7 guarding services."
      },
      closeProtection: {
        name: "Close Protection",
        description: "Close protection for VIPs and public figures."
      },
      patrols: {
        name: "Patrols & Interventions",
        description: "Security patrols and rapid response interventions."
      },
      nightSurveillance: {
        name: "Night Surveillance",
        description: "Night-time surveillance for private and corporate clients."
      },
      constructionSite: {
        name: "Construction Sites",
        description: "Security for construction sites and industrial facilities."
      },
      steward: {
        name: "Steward / Stadium Security",
        description: "Stewarding and security for stadiums and major sporting events."
      },
      secureTransport: {
        name: "Secure Transport",
        description: "Secure transport of goods and valuables."
      },
      cameraInstall: {
        name: "Camera Installation",
        description: "Installation of CCTV and video surveillance systems."
      },
      accessControl: {
        name: "Access Control",
        description: "Modern access control systems."
      },
      k9: {
        name: "K9 Security",
        description: "Canine units with trained security dogs."
      },
      remoteMonitoring: {
        name: "Remote Monitoring",
        description: "24/7 remote monitoring from our control centre."
      }
    },
    about: {
      titleParts: { prefix: "Who ", highlight: "are", suffix: " we?" },
      p1: "MAG Protection (Multy Assistance Group) positions itself as the leading group in security and assistance services in Luxembourg. Specialised in the protection of people and property, our company operates across every sector where security is a strategic priority.",
      p2: "Founded in Luxembourg, MAG Protection operates under a security licence issued by the Luxembourg State. Our team of qualified agents provides 24/7 coverage to guarantee the safety of our private, corporate and institutional clients."
    },
    stats: {
      missions: "Secured Missions",
      experience: "Years of Experience",
      availability: "Availability",
      clients: "Satisfied Clients"
    },
    testimonials: {
      titleParts: { prefix: "They ", highlight: "trust", suffix: " us" },
      thomas: {
        role: "Commercial Director",
        quote: "MAG Protection has been handling security for our events for 3 years. Exemplary professionalism and flawless responsiveness. I highly recommend them."
      },
      sophie: {
        role: "Property Manager",
        quote: "Thanks to MAG Protection's agents, our residents feel safe. Impeccable service, courteous and efficient staff."
      },
      jean: {
        role: "Event Organiser",
        quote: "For our major events in Luxembourg, MAG Protection is our essential partner. Always present, always professional."
      }
    },
    emergency: {
      title: "Need a Rapid Response?",
      subtitle: "Our response team is available 24/7",
      ctaEmergency: "Emergency Response:"
    },
    partners: {
      titleParts: { prefix: "Our ", highlight: "Partners", suffix: "" }
    },
    contact: {
      titleParts: { prefix: "Contact ", highlight: "Us", suffix: "" },
      phoneLabel: "Phone",
      emailLabel: "Email",
      form: {
        heading: "Send us a message",
        subheading: "Fill out the form below and we will get back to you shortly.",
        firstname: "First name",
        lastname: "Last name",
        company: "Company name",
        companyOptional: "(optional)",
        phone: "Phone",
        phonePlaceholder: "+352 661 000 000",
        email: "Email",
        message: "Message",
        messagePlaceholder: "Tell us about your needs...",
        consent: "I agree that my data will be used to process my request, in accordance with the privacy policy.",
        submit: "Send message",
        submitting: "Sending...",
        successTitle: "Message sent!",
        successBody: "Thank you for contacting us. We will get back to you shortly.",
        newMessage: "Send another message",
        errors: {
          firstname: "First name is required.",
          lastname: "Last name is required.",
          phone: "Invalid phone number.",
          email: "Invalid email address.",
          message: "Message must be at least 5 characters long.",
          consent: "You must accept the data processing terms.",
          rateLimit: "Too many messages sent. Please try again in a few minutes.",
          generic: "An error occurred. Please try again."
        }
      }
    },
    footer: {
      text: "Powered by MAG Protection © 2024 | magprotection.lu"
    }
  }
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem("mag_lang");
      return saved === "EN" || saved === "FR" ? saved : "FR";
    } catch {
      return "FR";
    }
  });

  const setLang = useCallback((newLang) => {
    if (newLang !== "FR" && newLang !== "EN") return;
    setLangState(newLang);
    try {
      localStorage.setItem("mag_lang", newLang);
    } catch {
      /* ignore */
    }
  }, []);

  const t = translations[lang];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return ctx;
};

export default LanguageContext;
